// THE ONE READING OF §3.5a's REWORK-DOOR PREVIEW, for both surfaces that offer
// a rework: §4.4's failure-action sheet (the resolve door) and §4.6's
// quarantine-board dialog (the hold door). D-299 owner rulings R8 and Q4.
//
// WHY ONE MODULE. The preview (`GET /v1/quality/inspections/{id}/rework-target`)
// answers for exactly the step the door would act on, chosen by the door's own
// function, with module 04's verdict on it and — for a HELD step — whether THIS
// caller could take the rework (D-133). Both surfaces must turn that answer
// into the same verdict and the same sentence: owner ruling Q4 says the dialog
// is judged "through the same rework-target read, with the same principal-aware
// sentences". Two copies of this reading are how the sheet and the board would
// drift apart while both suites stayed green, which is the class the round-3
// refutation kept finding.
//
// WHAT THIS MODULE DOES NOT DO: decide anything. Every verdict below is the
// server's (`blocked_by`, and since round 4 `permitted` / `holder_roles` /
// `requires_hold_release` — the same predicate the doors' refusals use for
// `continuations[].permitted`); this module only names it, and a preview that
// failed without a verdict is NOT a verdict — the caller leaves Confirm live
// and the door's own refusal speaks.

import type { TFunction } from "i18next";
import { RequestError } from "../../../../lib/request";
import type { QualityReworkTarget } from "../hooks/quality-queries";
import type { FailureActionOutcome } from "./inspection-run-port";
import { enumLabel } from "../../../../lib/enum-label";
import { joinSentences } from "../../../../lib/sentences";

/** What a surface knows about the step a rework would re-open, keyed by the
 * target it asked about so a late answer for a target the form no longer holds
 * is never applied to the one it does. `target === null` is the door's default
 * (no explicit ref: the inspection's own step). */
export type ReworkPreview =
  | { readonly target: string | null; readonly state: "checking" }
  | {
      readonly target: string | null;
      readonly state: "known";
      readonly result: QualityReworkTarget;
    }
  | { readonly target: string | null; readonly state: "unknown_step" }
  | { readonly target: string | null; readonly state: "unavailable" };

/** The judgement a surface renders. `undefined` from `reworkVerdictOf` means
 * "no verdict": Confirm stays live and the door decides. */
export type ReworkVerdict =
  | { readonly kind: "checking" }
  | { readonly kind: "unknown_step" }
  | { readonly kind: "live"; readonly seqNo: number }
  | {
      readonly kind: "order_not_live";
      /** Unknown when the verdict came from the inspection read (the own
       * step's order) rather than the preview. The sentence does not use it. */
      readonly seqNo: number | undefined;
      readonly workOrderStatus: string;
    }
  | {
      readonly kind: "operation_not_completed";
      readonly seqNo: number;
      readonly operationStatus: string;
    }
  | {
      readonly kind: "hold_release_required";
      readonly seqNo: number;
      /** The seed roles that take this rework at the door, the release code
       * included, as the server names them (D-133's shape) — empty when it
       * named none. */
      readonly holderRoles: readonly string[];
    }
  | {
      /** The caller lacks the door's OWN authority for this rework (the hold
       * door's role-lineage narrowing, D-224) — not a hold. Reachable only by a
       * principal the surface offered rework to on a fail-open or cloned
       * grant; worded with the holders all the same. */
      readonly kind: "not_permitted";
      readonly seqNo: number;
      readonly holderRoles: readonly string[];
    };

/** The door a preview is asked at — the wire's `door` query (D-299 Q4). */
export type ReworkPreviewDoor = "inspection_resolve" | "hold_disposition";

/** The door's own 422 for a target that does not resolve (§3.5a, the same
 * function answers the preview and the door). */
export const REWORK_TARGET_UNKNOWN_CODE = "QUALITY_SUBJECT_INVALID";

/** The sheet's preview (through the arm port, both shells) as a
 * `ReworkPreview`. Only the door's own 422 is a verdict ("no such step");
 * anything else — a dropped connection, a 5xx — is not a verdict on the step,
 * so it reads `unavailable` and the door answers at Confirm. */
export function reworkPreviewOfOutcome(
  target: string | null,
  outcome: FailureActionOutcome<QualityReworkTarget>,
): ReworkPreview {
  if (outcome.kind === "done") return { target, state: "known", result: outcome.result };
  return outcome.code === REWORK_TARGET_UNKNOWN_CODE
    ? { target, state: "unknown_step" }
    : { target, state: "unavailable" };
}

/** The §4.6 dialog's preview (a react-query read, office only) as a
 * `ReworkPreview`, by the same rule as `reworkPreviewOfOutcome`. */
export function reworkPreviewOfQuery(
  target: string | null,
  query: {
    readonly data: QualityReworkTarget | undefined;
    readonly error: unknown;
    readonly isPending: boolean;
  },
): ReworkPreview {
  if (query.data !== undefined) return { target, state: "known", result: query.data };
  if (query.error !== null && query.error !== undefined) {
    return query.error instanceof RequestError && query.error.code === REWORK_TARGET_UNKNOWN_CODE
      ? { target, state: "unknown_step" }
      : { target, state: "unavailable" };
  }
  return query.isPending ? { target, state: "checking" } : { target, state: "unavailable" };
}

/**
 * THE CALLER's half of the preview, read tolerantly (D-299 round 4; the
 * round-3 refutation's should-fix: the sheet told a planner "Rework will reopen
 * step N" on a held step the door then refused with D-133).
 *
 * Since round 4 the read is PRINCIPAL-AWARE at the `door` it was asked at:
 * `permitted` says whether THIS caller passes every authority layer that door
 * checks for this rework — `production.qc_hold.release` on a held step
 * included — `holder_roles` names the seed roles that do, and
 * `requires_hold_release` says the hold is why. That answer is the server's,
 * and this reader never re-derives it from a client grant: a mirror of the
 * rule is a second copy of it. Only an explicit `permitted: false` blocks; a
 * payload without it (a server from before round 4) yields no verdict, so the
 * door's own refusal speaks as it always has.
 */
export function authorityBlockOf(
  result: QualityReworkTarget,
): { readonly holderRoles: readonly string[]; readonly requiresHoldRelease: boolean } | undefined {
  const raw = result as unknown as Record<string, unknown>;
  if (raw["permitted"] !== false) return undefined;
  const roles = Array.isArray(raw["holder_roles"])
    ? raw["holder_roles"].filter((x): x is string => typeof x === "string" && x.length > 0)
    : [];
  return { holderRoles: roles, requiresHoldRelease: raw["requires_hold_release"] === true };
}

/** The verdict on one preview. Precedence is the door's own (spec 07 §3.5a as
 * amended by D-299 rulings G1/R2): module 04's re-open core first — the
 * order's reason, then the step's — and D-133's hold fence only for a step the
 * core would re-open. */
export function reworkVerdictOf(preview: ReworkPreview | undefined): ReworkVerdict | undefined {
  if (preview === undefined || preview.state === "unavailable") return undefined;
  if (preview.state === "checking") return { kind: "checking" };
  if (preview.state === "unknown_step") return { kind: "unknown_step" };
  const r = preview.result;
  if (r.blocked_by === "work_order_not_live") {
    return { kind: "order_not_live", seqNo: r.seq_no, workOrderStatus: r.work_order_status };
  }
  if (r.blocked_by === "operation_not_completed") {
    return {
      kind: "operation_not_completed",
      seqNo: r.seq_no,
      operationStatus: r.operation_status,
    };
  }
  // A blocked_by this client does not know (a newer server) is not a verdict
  // it can word — the door speaks. Read as `unknown`: the generated type says
  // the two values above are the only non-null ones, and the wire can outrun
  // the type.
  const unknownReason: unknown = r.blocked_by;
  if (unknownReason !== null) return undefined;
  const blocked = authorityBlockOf(r);
  if (blocked !== undefined) {
    return {
      kind: blocked.requiresHoldRelease ? "hold_release_required" : "not_permitted",
      seqNo: r.seq_no,
      holderRoles: blocked.holderRoles,
    };
  }
  return { kind: "live", seqNo: r.seq_no };
}

/** Which door's continuations a sentence names: the resolve door (§4.4 —
 * scrap / accept deviation) or the hold door (§4.6 — release with a
 * justification / scrap / downgrade; never accept deviation, ruling G2). */
export type ReworkDoor = "resolve" | "hold";

export interface ReworkSentenceOptions {
  readonly door: ReworkDoor;
  /** `quality.resolution.dispose` — whether the resolve door's scrap and
   * accept-deviation are the reader's own (owner ruling R3). */
  readonly canDispose: boolean;
  /** The reader's locale, for the list of role names. */
  readonly locale: string;
  /** Whether the form offers a field to name ANOTHER step. The sheet always
   * does; the §4.6 dialog does only for a manual quarantine, an inspection
   * that names no step, or after a target refusal. */
  readonly canNameTarget?: boolean;
}

/**
 * The sentence under a Confirm the verdict blocks — `undefined` for a verdict
 * that blocks nothing (`live`). One builder for both surfaces (ruling Q4).
 *
 * Principal-aware (owner ruling R3): on the resolve door the continuations are
 * named as the reader's own only when they hold `quality.resolution.dispose`;
 * on the hold door the dialog is only ever open to principals who hold the
 * disposition, so its continuations are theirs.
 */
export function reworkBlockedSentence(
  t: TFunction,
  verdict: ReworkVerdict | undefined,
  opts: ReworkSentenceOptions,
): string | undefined {
  if (verdict === undefined || verdict.kind === "live") return undefined;
  switch (verdict.kind) {
    case "checking":
      return t("quality.run.sheet.reworkTargetChecking");
    case "unknown_step":
      return t("quality.run.sheet.reworkTargetUnknown");
    case "order_not_live":
      return reworkOrderNotLiveSentence(t, verdict.workOrderStatus, opts);
    case "operation_not_completed": {
      const status = enumLabel(t, "production.status.op", verdict.operationStatus);
      // With no field to name another step (the §4.6 dialog on an
      // inspection's own step), "name the step whose work failed" would
      // invite an act the form does not offer; the hold door's own
      // continuations are named instead.
      return opts.door === "hold" && opts.canNameTarget !== true
        ? t("quality.board.reworkStepNotCompleted", { seq: verdict.seqNo, status })
        : t("quality.run.sheet.reworkTargetNotCompleted", { seq: verdict.seqNo, status });
    }
    case "hold_release_required":
      return holdReleaseSentence(t, verdict, opts.locale);
    case "not_permitted":
      return verdict.holderRoles.length === 0
        ? t("quality.run.sheet.reworkNotPermitted")
        : t("quality.run.sheet.reworkNotPermittedRoles", {
            roles: roleList(verdict.holderRoles, t, opts.locale),
          });
  }
}

/** The order's reason, worded for the door it would be refused at. */
export function reworkOrderNotLiveSentence(
  t: TFunction,
  workOrderStatus: string,
  opts: ReworkSentenceOptions,
): string {
  const status = enumLabel(t, "production.status.wo", workOrderStatus);
  if (opts.door === "hold") {
    return t(
      opts.canNameTarget === true
        ? "quality.board.reworkTargetNotLive"
        : "quality.board.reworkOrderNotLive",
      { status },
    );
  }
  return t(
    opts.canDispose
      ? "quality.run.sheet.reworkTargetNotLive"
      : "quality.run.sheet.reworkTargetNotLiveNoDispose",
    { status },
  );
}

/**
 * D-133's who-sentence: the step is held, this reader cannot clear the hold,
 * and a holder of `production.qc_hold.release` takes the rework (or clears the
 * hold first). Never "will reopen" — the door would refuse it with
 * `QUALITY_REWORK_REQUIRES_HOLD_RELEASE`. The holders are the server's list,
 * each rendered through the one role-label reader's keys (`roles.<code>`), so
 * a seed role reads as its localized name; with no list, the sentence names
 * the seed set in words. The ask that closes it is its own key, joined on.
 */
export function holdReleaseSentence(
  t: TFunction,
  verdict: Extract<ReworkVerdict, { kind: "hold_release_required" }>,
  locale: string,
): string {
  const head =
    verdict.holderRoles.length === 0
      ? t("quality.run.sheet.reworkHeldNoRelease", { seq: verdict.seqNo })
      : t("quality.run.sheet.reworkHeldNoReleaseRoles", {
          seq: verdict.seqNo,
          roles: roleList(verdict.holderRoles, t, locale),
        });
  // Two keys under the copy budget (D-425), one sentence on the glass.
  return joinSentences([head, t("quality.run.sheet.reworkHeldAsk")], locale);
}

/** The server's role codes as one "a, b or c" list in the reader's words,
 * each through the system-role labels (`roles.<code>`); a code with no label
 * (a tenant's own role) prints as the server sent it, never as a raw key. */
function roleList(codes: readonly string[], t: TFunction, locale: string): string {
  return new Intl.ListFormat(locale, { type: "disjunction" }).format(
    codes.map((code) => t(`roles.${code}`, { defaultValue: code })),
  );
}
